'use client'

import {
	CONTEXTUAL_MESSAGE_TYPE,
	ContextualMessage,
} from '@fukku/contextual-message/ContextualMessage'
import { sendGenericEvent } from 'analytics/events/generics/sendGenericEvent'
import { cx } from 'classix'
import { useIntersectionObserver } from 'hooks/useIntersectionObserver/useIntersectionObserver'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { replacePlaceholders } from 'labels/lib/replacePlaceholders'
import { GenericEvents } from 'product/types/GenericEvents'
import type { ProductGenderId } from 'product/types/Product'
import { useEffect, useRef, useState } from 'react'
import { useModelInfo } from '../hooks/useModelInfo'
import type { Color, Look } from '../types/Color'
import { getGenderKey } from '../utils/getGender/getGenderKey'
import styles from './ModelInfo.module.css'

interface ModelInfoProps {
	readonly selectedColor?: Color
	readonly selectedLook?: Look
	readonly genderId?: ProductGenderId
	readonly className?: string
}

const defaultGender = 'M'

export const ModelInfo = ({
	className,
	selectedColor,
	selectedLook,
	genderId,
}: ModelInfoProps) => {
	const { t } = useLabels()
	const { modelHeight, modelSizeCode } = useModelInfo({
		selectedColor,
		selectedLook,
	})
	const ref = useRef<HTMLDivElement | null>(null)
	const entry = useIntersectionObserver(ref, {
		threshold: 0,
	})
	const [sentAnalytics, setSentAnalytics] = useState(false)

	let message = ''

	const showModelInfo = modelHeight && modelSizeCode

	useEffect(() => {
		if (
			!sentAnalytics &&
			!selectedColor?.disclaimer &&
			showModelInfo &&
			entry?.isIntersecting
		) {
			sendGenericEvent(GenericEvents.SHOW_MODEL_INFO)
			setSentAnalytics(true)
		}
	}, [selectedColor, showModelInfo, entry, sentAnalytics])

	if (selectedColor?.disclaimer || !showModelInfo) {
		return null
	}

	if (modelHeight && modelSizeCode) {
		const placeholderValues = {
			1: modelSizeCode,
			2: modelHeight,
		}
		const key = getGenderKey(genderId ?? defaultGender)
		if (!key) {
			return null
		}
		message = replacePlaceholders(t(key), placeholderValues)
	}

	return (
		<div className={cx(styles.modelInfo, className)} ref={ref}>
			<ContextualMessage
				className={styles.message}
				type={CONTEXTUAL_MESSAGE_TYPE.DEFAULT}
				message={message}
			/>
		</div>
	)
}
